import { env } from "@/env";
import type { CartResponse } from "@/networking/carts.types";
import Cookies from "js-cookie";
import { z } from "zod";

export function createApiResponseZod<T extends z.ZodType>(dataSchema: T) {
  return z.object({
    data: dataSchema,
    status: z.string(),
  });
}
export function getApiHeaders() {
  return {
    "Content-Type": "application/json",
    "x-fluid-client": "checkout",
  };
}

const SERVER_ERROR_MESSAGE =
  "The server encountered an error. Please try again later.";

export const apiErrorSchema = z.object({
  message: z.string(),
  status: z.number(),
  data: z.unknown().optional(),
});

export async function fetchApi<
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
>({
  endpoint,
  method = "POST",
  input,
  inputSchema,
  outputSchema,
  fetchOptions = {},
}: {
  endpoint: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  input?: z.infer<TInput>;
  inputSchema?: TInput;
  outputSchema: TOutput;
  fetchOptions?: RequestInit;
}): Promise<z.infer<TOutput>> {
  if (input && inputSchema) {
    inputSchema.parse(input);
  }

  let url = endpoint.startsWith("http")
    ? endpoint
    : `${env.NEXT_PUBLIC_FLUID_API_URL}${endpoint}`;

  if (method === "GET" && input) {
    const params = new URLSearchParams();
    Object.entries(input).forEach(([key, value]) => {
      params.append(key, String(value));
    });
    url += `?${params.toString()}`;
  }

  const { headers: extraHeaders, ...restFetchOptions } = fetchOptions ?? {};
  const merged = new Headers(getApiHeaders());
  if (extraHeaders) {
    new Headers(extraHeaders).forEach((value, key) => {
      merged.set(key, value);
    });
  }
  const response = await fetch(url, {
    method,
    headers: merged,
    ...(input && method !== "GET" && { body: JSON.stringify(input) }),
    ...restFetchOptions,
  });

  // 3xx/4xx/5xx: read as text then parse — avoids raw JSON parse error when server returns HTML (e.g. 500 error page).
  if (response.status >= 300) {
    const text = await response.text();
    const contentType = response.headers.get("content-type");
    const isJson =
      contentType?.includes("application/json") ||
      contentType?.includes("application/vnd.api+json");
    let data: unknown = null;
    if (text && isJson) {
      try {
        data = JSON.parse(text) as unknown;
      } catch {
        data = null;
      }
    }

    if (response.status === 401 && Cookies.get("jwt")) {
      Cookies.remove("jwt");
      window.location.reload();
    }

    if (response.status === 302) {
      return data as z.infer<TOutput>;
    }

    // Redirect response with redirect_url in body is valid — return as success.
    if (
      data != null &&
      typeof data === "object" &&
      "redirect_url" in (data as object)
    ) {
      return data as z.infer<TOutput>;
    }

    // Extract error message from common API shapes; 5xx gets a friendly English message (no i18n in this layer).
    const dataObj =
      typeof data === "object" && data !== null
        ? (data as Record<string, unknown>)
        : null;
    const message =
      typeof dataObj?.error_message === "string"
        ? dataObj.error_message
        : typeof dataObj?.error === "string"
          ? dataObj.error
          : typeof dataObj?.message === "string"
            ? dataObj.message
            : response.status >= 500
              ? SERVER_ERROR_MESSAGE
              : `${method} request failed`;
    throw apiErrorSchema.parse({
      message,
      status: response.status,
      data: data ?? undefined,
    });
  }

  // 2xx: original path — expect JSON, no change to success behavior.
  const data = await response.json();
  const result = outputSchema.safeParse(data);
  if (!result.success) {
    console.error(`error parsing api response. ${endpoint}`, result.error);
    return data as z.infer<TOutput>;
  }
  return result.data as z.infer<TOutput>;
}

export async function fetchApiFormData<
  TInput extends z.ZodType,
  TOutput extends z.ZodType,
>({
  endpoint,
  method = "POST",
  input,
  inputSchema,
  outputSchema,
}: {
  endpoint: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  input?: z.infer<TInput>;
  inputSchema?: TInput;
  outputSchema: TOutput;
}): Promise<z.infer<TOutput>> {
  if (input && inputSchema) {
    inputSchema.parse(input);
  }

  let url = endpoint.startsWith("http")
    ? endpoint
    : `${env.NEXT_PUBLIC_FLUID_API_URL}${endpoint}`;

  if (method === "GET" && input) {
    const params = new URLSearchParams();
    Object.entries(input).forEach(([key, value]) => {
      params.append(key, String(value));
    });
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    method,
    headers: { "x-fluid-client": "checkout" },
    ...(input && method !== "GET" && { body: input as BodyInit }),
  });

  const text = await response.text();
  const contentType = response.headers.get("content-type");
  const isJson =
    contentType?.includes("application/json") ||
    contentType?.includes("application/vnd.api+json");
  let data: unknown = null;
  if (text && isJson) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const dataObj =
      typeof data === "object" && data !== null
        ? (data as Record<string, unknown>)
        : null;
    const message =
      typeof dataObj?.error_message === "string"
        ? dataObj.error_message
        : typeof dataObj?.error === "string"
          ? dataObj.error
          : typeof dataObj?.message === "string"
            ? dataObj.message
            : response.status >= 500
              ? SERVER_ERROR_MESSAGE
              : `${method} request failed`;
    throw apiErrorSchema.parse({
      message,
      status: response.status,
      data: data ?? undefined,
    });
  }

  // 2xx with non-JSON or parse failure: response-format problem, not a server error
  if (data === null || typeof data !== "object") {
    throw apiErrorSchema.parse({
      message: `${method} request returned an unexpected response format`,
      status: response.status,
      data: undefined,
    });
  }

  const result = outputSchema.safeParse(data);
  if (!result.success) {
    // Sentry.captureMessage("error parsing api response", {
    //   extra: {
    //     input,
    //     endpoint,
    //     method,
    //     url,
    //     response: data,
    //     error: result.error,
    //   },
    // });
    console.error(`error parsing api response. ${endpoint}`, result.error);
    return data as z.infer<TOutput>;
  }
  return result.data as z.infer<TOutput>;
}

export type ApiError = {
  message: string;
  status?: number;
  data?: {
    error_message?: string;
    errors?: Record<string, string[]>;
  };
};

/** True only for errors thrown by fetchApi (have status). Rejects plain Error/TypeError e.g. "Failed to fetch". */
export function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === "object" &&
    err !== null &&
    "message" in err &&
    typeof (err as Record<string, unknown>).message === "string" &&
    "status" in err &&
    typeof (err as Record<string, unknown>).status === "number" &&
    (!("data" in err) ||
      typeof (err as Record<string, unknown>).data !== "object" ||
      (err as Record<string, unknown>).data === null ||
      "error_message" in ((err as Record<string, unknown>).data as object) ||
      "errors" in ((err as Record<string, unknown>).data as object))
  );
}

// extracts first message from API 422 response errors.subscription array.
export function getSubscriptionErrorMessage(
  error: unknown,
): string | undefined {
  if (
    !isApiError(error) ||
    !error.data ||
    typeof error.data !== "object" ||
    !Array.isArray(
      (error.data as { errors?: { subscription?: string[] } }).errors
        ?.subscription,
    )
  ) {
    return undefined;
  }
  return (error.data as { errors: { subscription: string[] } }).errors
    .subscription[0];
}

export const hasCartItemErrors = (cart: CartResponse["cart"] | null) => {
  if (!cart) return false;
  return cart.items?.some((item) => item.errors && item.errors.length > 0);
};

export const hasCartErrors = (cart: CartResponse["cart"] | null) => {
  if (!cart) return false;

  return cart.messages?.errors && cart.messages?.errors?.length > 0;
};
